/**
 * The install moment (`ProductOverview.md` §4.8).
 *
 * "Install is suggested exactly once, softly, after a successful session, when the product has
 * just proven its worth." Every word of that is a constraint:
 *
 *   exactly once   a flag in localStorage, set when it is shown, never cleared
 *   softly         a line and a button on a screen the user is already done with, never a modal
 *   after          only ever offered from the convergence screen
 *
 * Chrome hands us a deferred `beforeinstallprompt`; iOS has no such event, so there the honest
 * answer is to say where the button is.
 */

const ASKED_KEY = 'beeline:install-asked';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;

/** Call once at startup: the event fires early and only once, so it has to be caught. */
export function watchForInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own banner, which is not "softly".
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    markAsked();
  });
}

const alreadyRunningInstalled = (): boolean => {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
};

const hasAsked = (): boolean => {
  try {
    return window.localStorage.getItem(ASKED_KEY) === '1';
  } catch {
    // No storage means we cannot promise "exactly once", so we do not ask at all.
    return true;
  }
};

function markAsked(): void {
  try {
    window.localStorage.setItem(ASKED_KEY, '1');
  } catch {
    // Nothing to do; the check above already errs towards not asking again.
  }
}

export type InstallOffer =
  /** Chrome and friends: a real prompt we can trigger. */
  | { kind: 'prompt'; accept: () => Promise<void> }
  /** iOS: no API, so tell them where the button is. */
  | { kind: 'instructions'; text: string }
  | null;

/**
 * What, if anything, to offer. Returns null when it has been asked before, when the app is
 * already installed, or when there is nothing useful to say.
 */
export function installOffer(): InstallOffer {
  if (alreadyRunningInstalled() || hasAsked()) return null;

  if (deferred) {
    const event = deferred;
    return {
      kind: 'prompt',
      accept: async () => {
        markAsked();
        deferred = null;
        await event.prompt();
        await event.userChoice;
      },
    };
  }

  // iOS Safari can install, but only through the share sheet, and only in Safari itself.
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  if (isIos && isSafari) {
    return {
      kind: 'instructions',
      text: 'Share → Add to Home Screen, and Beeline opens like an app next time.',
    };
  }

  return null;
}

/** Called when the offer has been shown, so "exactly once" counts showing rather than tapping. */
export const noteInstallOffered = markAsked;
